/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import { CHANNELS } from "@nf/constants";
import * as React from "react";
import { PlatformType } from "../enums";
import { PlatformHelper, PlatformTypeHelper } from "../helpers";
import {
  IConfigurationBrandingModel,
  IConfigurationModel,
  IListComponentItemPlaceholder,
} from "../models";

const defaultBranding: IConfigurationBrandingModel = {
  Id: CHANNELS.NF_PLUS,
  AppPaddingTop: 100,
  AppPaddingRight: 62,
  AppPaddingBottom: 64,
  AppPaddingLeft: 55,
  AppListPaddingTop: 70,
  AppListPaddingBottom: 70,
  AppListItemPaddingHorizontal: 20,
  AppBackgroundColor: "#000000ff",
  AppCellsBackgroundColor: "#212640ff",
  AppFontFamily: "Montserrat",
  AppFontSize: 16,
  AppPrimaryColor: "#ffa000ff",
  AppPrimaryTextColor: "#ffffffff",
  AppSecondaryColor: "#3c4573ff",
  AppSecondaryTextColor: "#d6d6d6ff",
};

const template1: IConfigurationBrandingModel = {
  Id: CHANNELS.NF_PLUS,
  AppPaddingTop: 100,
  AppPaddingRight: 62,
  AppPaddingBottom: 64,
  AppPaddingLeft: 55,
  AppListPaddingTop: 70,
  AppListPaddingBottom: 70,
  AppListItemPaddingHorizontal: 20,
};

const templateSmartTV: IConfigurationBrandingModel = Object.assign(
  {},
  defaultBranding,
  {
    Id: CHANNELS.NF_PLUS,
    AppTemplate: "APP_VERTICAL_MENU",
    AppFontFamily: "Raleway",
    AppPaddingTop: 50,
    AppPaddingRight: 90,
    AppPaddingBottom: 64,
    AppPaddingLeft: 10,
    AppListPaddingTop: 32,
    AppListPaddingBottom: 35,
    AppListItemPaddingHorizontal: 37,
    AppMenuWidth: 195,
    AppCarouselPortraitHeight: 255,
    AppCarouselPortraitWidth: 176,
    AppCarouselLandscapeHeight: 168,
    AppCarouselLandscapeWidth: 312,
    AppBackgroundColor: "#18161F",
    AppCellsBackgroundColor: "#383346",
    AppPrimaryColor: "#EAFF00",
    AppPrimaryTextColor: "#fff",
    AppSecondaryTextColor: "#38324C",
    IsDefault: true,
  }
);

enum ScreenWidth {
  SD = 640,
  HD = 1280,
  FHD = 1920,
  UHD = 3860,
}

export class ThemeProvider {
  private defaultBrandingKey: string = CHANNELS.NF_PLUS;

  private defaultBranding: IConfigurationBrandingModel = template1;

  private branding: IConfigurationBrandingModel = template1;

  private brandings: { [key: string]: IConfigurationBrandingModel } = {};

  public setBrandings(
    configuration?: IConfigurationModel,
    platformType?: PlatformType
  ) {
    if (!platformType) {
      platformType = PlatformTypeHelper.getValue(
        `${
          process.env.NEXT_PUBLIC_PLATFORM_CODE ||
          process.env.REACT_APP_PLATFORM_CODE
        }`,
        PlatformHelper.Platform
      );
    }

    switch (platformType) {
      case PlatformType.Tizen:
      case PlatformType.TitanOS:
      case PlatformType.WebOS:
        this.defaultBranding = templateSmartTV;
        break;
      default:
        this.defaultBranding = template1;
        break;
    }

    if (configuration && configuration.Brandings) {
      for (const brandingKey in configuration.Brandings) {
        const brandingData = configuration.Brandings[brandingKey];

        this.brandings[brandingKey] = brandingData;

        if (brandingData.IsDefault) {
          this.defaultBrandingKey = brandingKey;
          this.defaultBranding = Object.assign(
            {},
            this.defaultBranding,
            brandingData
          );
        }
      }
    } else if (configuration && configuration.Branding) {
      this.defaultBranding = Object.assign(
        {},
        this.defaultBranding,
        configuration.Branding
      );
    }

    if (!this.branding || this.branding.Id === CHANNELS.NF_PLUS) {
      this.branding = Object.assign({}, this.defaultBranding);
    }
  }

  public setCurrentBranding(brandingId?: string): boolean {
    const brandingKey = brandingId ?? this.defaultBrandingKey;
    const brandingData = this.brandings[brandingKey.toUpperCase()];
    if (this.branding.Id !== brandingKey) {
      if (brandingData) {
        this.branding = Object.assign({}, this.defaultBranding, brandingData);
        return true;
      }
    }
    return false;
  }

  public getBranding(): IConfigurationBrandingModel {
    return this.branding;
  }

  public getSmartTVLogoUrl = () => {
    return this.branding.WebHeaderLogoUrl;
  };

  public getColor(key: keyof IConfigurationBrandingModel) {
    let value = <string>this.branding[key];

    return value;
  }

  public getListItemCountFactor(itemCount: number) {
    switch (itemCount) {
      case 1:
        return 2;
      case 2:
        return 1.8;
      case 3:
        return 1.4;
      case 4:
        return 1;
      case 5:
        return 0.8;
      case 6:
        return 0.6;
      default:
        return 1;
    }
  }

  public getResolutionFactor() {
    const { width } = this.getDocumentSize();
    let factor;

    if (width <= ScreenWidth.SD) {
      factor = 0.5;
    } else if (width <= ScreenWidth.HD) {
      factor = 0.7;
    } else if (width <= ScreenWidth.FHD) {
      factor = 1;
    } else {
      factor = 1.25;
    }

    return factor;
  }

  public getFontFamily() {
    return this.branding.AppFontFamily;
  }

  public getFontSize() {
    const baseFontSize = 16;
    const factor = this.getResolutionFactor();

    return baseFontSize * factor;
  }

  public getFontFaces() {
    return this.branding.AppFontFaces || [];
  }

  public getAppFavIconUrl() {
    return this.branding.AppFavIconUrl;
  }

  public getDocumentSize() {
    if (typeof document !== "undefined") {
      const documentElementHeight = document.documentElement
        ? document.documentElement.clientHeight
        : 0;
      const documentElementWidth = document.documentElement
        ? document.documentElement.clientWidth
        : 0;

      return {
        height: document.body
          ? document.body.clientHeight
          : documentElementHeight,
        width: document.body ? document.body.clientWidth : documentElementWidth,
      };
    } else {
      return {
        height: 0,
        width: 0,
      };
    }
  }

  public getListComponentItemPlaceholder(): IListComponentItemPlaceholder {
    return {
      Type: this.branding.AppListItemPlaceholderType ?? "IMAGE",
      Value: this.branding.AppListItemPlaceholderValue,
    };
  }
}

export interface IThemeContext {
  themeProvider: ThemeProvider;
  onBrandingChange?: (brandingId?: string) => void;
}

export const ThemeContext = React.createContext<IThemeContext>({
  themeProvider: new ThemeProvider(),
});
